import React from 'react';
import { Plus } from 'lucide-react';
import { UserSearchInput } from './components/UserSearchInput';
import { TO_UFS_INPUT_CLASS, TO_UFS_INPUT_CLASS_DISABLED } from './constants';

/**
 * Combined To + optional Verification row(s) + UFS column: stores user IDs for backend; displays names in the UI.
 * Imprest: **To** = APPROVAL; **Verification** rows = VERIFICATION steps (definition order); **ufsList** = extra assignees
 * mapped to the **DYNAMIC** step on submit (bottom part of the UFS column when {@link verificationAfterUfs} is true).
 * Task order on the server: **DYNAMIC** first (list reversed so the last row acts first), **VERIFICATION** next (definition
 * `order` ascending — first verification step in the definition after DYNAMIC), then **APPROVAL**.
 * With {@link verificationAfterUfs}: under one **UFS** label, render **verification** pickers first, then **DYNAMIC** rows.
 * Use {@link hideVerificationSlotLabels} to drop step-name labels; {@link verificationRowsHighestOrderFirst} to show highest
 * definition-order verification at the **top** (form indices unchanged for submit).
 * Fields are editable only when disabled=false (typically when workflow status is DRAFT or RETURNED).
 * @param {Object} props
 * @param {{ to: string, ufsList: string[], toDisplayName?: string, ufsDisplayNames?: string[], verificationList?: string[], verificationDisplayNames?: string[] }} props.value
 * @param {function(Object): void} props.onChange
 * @param {number[]|null|undefined} [props.verificationSlotIndices] — when non-null, only these definition indices render
 */
export const ToAndUfs = ({
  labelTo = 'To',
  labelUfs = 'UFS',
  labelVerification = 'Verification',
  value,
  onChange,
  required = false,
  placeholderTo = 'Recipient',
  placeholderUfs = 'Add UFS entry',
  placeholderVerification = 'Select user',
  disabled = false,
  lockTo = false,
  lockedUfsIndexes = [],
  lockedVerificationIndexes = [],
  errorTo,
  errorUfs,
  errorVerification,
  /** When > 0, renders `verificationList[0..count-1]` between To and UFS (Imprest). */
  verificationSlotCount = 0,
  /**
   * When set (including `[]`), only these definition indices render as pickers; each still reads/writes
   * `verificationList[index]`. When `undefined`, all slots `0..verificationSlotCount-1` render (legacy).
   */
  verificationSlotIndices,
  /** Optional label per slot (e.g. step name from definition). */
  verificationLabels = [],
  /** When true, under the UFS column heading: verification rows first, then dynamic (ufsList) rows. */
  verificationAfterUfs = false,
  /** Hide per-slot labels (step names); use placeholders / aria-label only. */
  hideVerificationSlotLabels = false,
  /**
   * When true, render verification pickers **highest definition `order` first** (top), lowest last (bottom).
   * Form indices stay definition-ascending: top row edits `verificationList[last]`.
   */
  verificationRowsHighestOrderFirst = false,
  labelWidthRem = 6,
  labelPosition = 'left',
}) => {
  const {
    to = '',
    ufsList = [''],
    toDisplayName = '',
    ufsDisplayNames = [],
    verificationList = [],
    verificationDisplayNames = [],
  } = value || {};
  const items = Array.isArray(ufsList) ? ufsList : ufsList ? [ufsList] : [''];
  const displayNames = Array.isArray(ufsDisplayNames) ? ufsDisplayNames : [];
  const verItems = Array.from(
    { length: Math.max(0, Number(verificationSlotCount) || 0) },
    (_, i) => (Array.isArray(verificationList) ? verificationList[i] : '') ?? ''
  );
  const verDisplayNames = Array.isArray(verificationDisplayNames) ? verificationDisplayNames : [];
  const isTop = labelPosition === 'top';
  const gridStyle = isTop ? undefined : { gridTemplateColumns: `${labelWidthRem}rem 1fr` };
  const lockedSet = new Set(Array.isArray(lockedUfsIndexes) ? lockedUfsIndexes : []);
  const lockedVerSet = new Set(Array.isArray(lockedVerificationIndexes) ? lockedVerificationIndexes : []);

  const isToLocked = Boolean(lockTo);
  const isUfsRowLocked = (idx) => lockedSet.has(idx);

  const handleToChange = (id, displayName) => {
    if (disabled || isToLocked) return;
    onChange({
      to: id ?? '',
      ufsList: items,
      toDisplayName: displayName ?? '',
      ufsDisplayNames: displayNames,
      verificationList: verItems,
      verificationDisplayNames: verDisplayNames,
    });
  };

  const handleUfsItemChange = (idx, id, displayName) => {
    if (disabled || isUfsRowLocked(idx)) return;
    const next = [...items];
    const nextDisplay = [...displayNames];
    next[idx] = id ?? '';
    nextDisplay[idx] = displayName ?? '';
    onChange({
      to,
      ufsList: next,
      toDisplayName,
      ufsDisplayNames: nextDisplay,
      verificationList: verItems,
      verificationDisplayNames: verDisplayNames,
    });
  };

  const handleUfsAdd = () =>
    onChange({
      to,
      ufsList: [...items, ''],
      toDisplayName,
      ufsDisplayNames: [...displayNames, ''],
      verificationList: verItems,
      verificationDisplayNames: verDisplayNames,
    });
  const handleUfsRemove = (idx) => {
    if (isUfsRowLocked(idx)) return;
    if (items.length <= 1) return;
    const next = items.filter((_, i) => i !== idx);
    const nextDisplay = displayNames.filter((_, i) => i !== idx);
    onChange({
      to,
      ufsList: next,
      toDisplayName,
      ufsDisplayNames: nextDisplay,
      verificationList: verItems,
      verificationDisplayNames: verDisplayNames,
    });
  };

  const handleVerificationChange = (idx, id, displayName) => {
    if (disabled || lockedVerSet.has(idx)) return;
    const next = [...verItems];
    const nextDisp = [...verDisplayNames];
    next[idx] = id ?? '';
    nextDisp[idx] = displayName ?? '';
    onChange({
      to,
      ufsList: items,
      toDisplayName,
      ufsDisplayNames: displayNames,
      verificationList: next,
      verificationDisplayNames: nextDisp,
    });
  };

  const labelToEl = (
    <label className="text-sm font-medium text-primary-dark">
      {labelTo} {required && <span className="text-red-500">*</span>}
    </label>
  );
  const labelUfsEl = (
    <label className="text-sm font-medium text-primary-dark">{labelUfs}</label>
  );

  const toInputClass = (disabled || isToLocked) ? TO_UFS_INPUT_CLASS_DISABLED : TO_UFS_INPUT_CLASS;
  const toField = (
    <UserSearchInput
      value={to}
      valueDisplay={toDisplayName}
      onChange={handleToChange}
      placeholder={placeholderTo}
      disabled={disabled || isToLocked}
      error={errorTo}
      inputClassName={toInputClass}
    />
  );

  const ufsInputRow = (item, idx) => {
    const rowLocked = disabled || isUfsRowLocked(idx);
    return (
    <div key={idx} className="min-w-0 flex gap-2 items-center w-full">
      <div className="min-w-0 flex-1">
        <UserSearchInput
          value={item}
          valueDisplay={displayNames[idx]}
          onChange={(id, name) => handleUfsItemChange(idx, id, name)}
          placeholder={placeholderUfs}
          disabled={rowLocked}
          inputClassName={rowLocked ? TO_UFS_INPUT_CLASS_DISABLED : TO_UFS_INPUT_CLASS}
        />
      </div>
      {idx === items.length - 1 ? (
        <button
          type="button"
          onClick={handleUfsAdd}
          disabled={disabled}
          className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg text-primary-dark border border-gray-300 bg-white hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          aria-label="Add UFS row"
        >
          <Plus className="h-4 w-4" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => handleUfsRemove(idx)}
          disabled={disabled || isUfsRowLocked(idx)}
          className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg text-red-600 border border-gray-300 bg-white hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
          aria-label="Remove"
        >
          ×
        </button>
      )}
    </div>
    );
  };

  const ufsContent = (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={idx}>{ufsInputRow(item, idx)}</div>
      ))}
      {errorUfs && <p className="text-xs text-red-600">{errorUfs}</p>}
    </div>
  );

  const verificationRows = (() => {
    const n = Math.max(0, Number(verificationSlotCount) || 0);
    const useExplicitIndices = verificationSlotIndices != null;
    const slotIndicesAsc = (() => {
      if (!useExplicitIndices) {
        if (n === 0) return [];
        return Array.from({ length: n }, (_, i) => i);
      }
      if (n === 0) return [];
      const raw = Array.isArray(verificationSlotIndices) ? verificationSlotIndices : [];
      const filtered = raw.filter(
        (i) => Number.isFinite(Number(i)) && Number(i) >= 0 && Number(i) < n,
      );
      return [...new Set(filtered.map((x) => Number(x)))].sort((a, b) => a - b);
    })();
    if (slotIndicesAsc.length === 0) return null;
    const slotIndicesVisual = verificationRowsHighestOrderFirst
      ? [...slotIndicesAsc].reverse()
      : slotIndicesAsc;
    const rows = [];
    const visualCount = slotIndicesVisual.length;
    for (let v = 0; v < visualCount; v += 1) {
      const idx = slotIndicesVisual[v];
      const slotLabel =
        hideVerificationSlotLabels
          ? null
          : (Array.isArray(verificationLabels) && String(verificationLabels[idx] ?? '').trim()
              ? String(verificationLabels[idx])
              : `${labelVerification}:`);
      const rowLocked = disabled || lockedVerSet.has(idx);
      const inputEl = (
        <UserSearchInput
          value={verItems[idx] ?? ''}
          valueDisplay={verDisplayNames[idx]}
          onChange={(id, name) => handleVerificationChange(idx, id, name)}
          placeholder={placeholderVerification}
          disabled={rowLocked}
          inputClassName={rowLocked ? TO_UFS_INPUT_CLASS_DISABLED : TO_UFS_INPUT_CLASS}
          aria-label={
            hideVerificationSlotLabels
              ? `${placeholderVerification}${visualCount > 1 ? ` (${v + 1} of ${visualCount})` : ''}`
              : undefined
          }
        />
      );
      if (slotLabel) {
        rows.push(
          <div key={`ver-${idx}`} className="grid items-center gap-y-1" style={gridStyle}>
            <label className="text-sm font-medium text-primary-dark">{slotLabel}</label>
            {inputEl}
          </div>
        );
      } else {
        rows.push(
          <div key={`ver-${idx}`} className="min-w-0 w-full">
            {inputEl}
          </div>
        );
      }
    }
    return (
      <div className="space-y-2 w-full">
        {rows}
        {errorVerification && (
          <p className="text-xs text-red-600">{errorVerification}</p>
        )}
      </div>
    );
  })();

  if (isTop) {
    return (
      <div className="space-y-3 w-full">
        <div className="space-y-1">
          {labelToEl}
          {toField}
        </div>
        {verificationAfterUfs ? (
          <div className="space-y-2">
            {labelUfsEl}
            {verificationRows}
            {ufsContent}
          </div>
        ) : (
          <>
            {verificationRows}
            <div className="space-y-2">
              <label className="text-sm font-medium text-primary-dark">{labelUfs}</label>
              {ufsContent}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2 w-full">
      <div className="grid items-center gap-y-1" style={gridStyle}>
        {labelToEl}
        {toField}
      </div>
      {verificationAfterUfs ? (
        <div className="grid items-start gap-y-1" style={gridStyle}>
          {labelUfsEl}
          <div className="space-y-2 min-w-0">
            {verificationRows}
            {ufsContent}
          </div>
        </div>
      ) : (
        <>
          {verificationRows}
          <div className="grid items-start gap-y-1" style={gridStyle}>
            {labelUfsEl}
            {ufsContent}
          </div>
        </>
      )}
      {errorTo && (
        <p className="text-xs text-red-600 col-span-2">{errorTo}</p>
      )}
    </div>
  );
};

export default ToAndUfs;
